<template>
  <div
    data-slot="social-page"
    :class="[ 'flex min-w-0 items-center', onlyIcon ? '' : 'gap-3' ]"
  >
    <template v-if="isSkeleton">
      <ui-skeleton :class="[ 'shrink-0 rounded-full', small ? 'size-7' : 'size-9' ]" />
      <ui-skeleton v-if="!onlyIcon" class="h-3.5 w-24" />
    </template>

    <template v-else>
      <div class="flex shrink-0 flex-row-reverse justify-end">
        <div
          v-for="(sourceProfile, index) in computedSourceProfiles"
          :key="'social-page--image--' + sourceProfile.external_id"
          :class="[ 'relative', index > 0 ? '-mr-1.5' : '', sourceProfile.deleted_at ? 'saturate-[.35] contrast-50 brightness-[1.42]' : '' ]"
        >
          <lazy-image
            v-if="lazyload"
            :class="[ 'rounded-full border-2 border-card shadow-sm', small ? 'size-7 min-w-7' : 'size-9 min-w-9' ]"
            :src="sourceProfile.picture"
          />
          <img
            v-else
            :class="[ 'rounded-full border-2 border-card object-cover shadow-sm', small ? 'size-7 min-w-7' : 'size-9 min-w-9' ]"
            :src="sourceProfile.picture"
            alt=""
          >
          <span :class="[ 'absolute -right-1.5 -bottom-1 flex items-center justify-center rounded-full bg-card shadow-sm', small ? 'size-3.5' : 'size-4.5' ]">
            <i :class="[ 'icon uncolored', small ? 'size-2.5' : 'size-3.5', sourceProfile.platform + '-color' ]" />
          </span>
        </div>
      </div>

      <div v-if="!onlyIcon" :class="[ 'flex min-w-0 flex-col', inactive ? 'opacity-50' : '' ]">
        <span v-if="date" class="w-fit text-[11px] leading-tight text-muted-foreground">
          <span v-tooltip="dateString">{{ dateAgoString }}</span>
          <span v-if="dateSuffix"> &middot; {{ dateSuffix }}</span>
        </span>
        <span class="truncate text-sm text-foreground">{{ computedName }}</span>
      </div>
    </template>
  </div>
</template>

<script>
import SourceProfile from '@/entities/SourceProfile.js';
import LazyImage from '@/ui/components/LazyImage.vue';
import { formatTime, formatToStringYMD, timeAgo } from '@/utils/date';

/** Profile picture(s) with platform badges, plus the name and an optional date. */
export default {
  name: 'SocialPage',
  components: {
    LazyImage,
  },
  props: {
    sourceProfile: {
      type: [ SourceProfile, null ],
      required: false,
      default: () => null,
    },
    sourceProfiles: {
      type: [ Array, null ],
      required: false,
      default: () => null,
    },
    date: {
      type: [ Date, null ],
      required: false,
      default: () => null,
    },
    dateTooltipPrefix: {
      type: String,
      required: false,
      default: '',
    },
    dateSuffix: {
      type: [ String, null ],
      required: false,
      default: () => null,
    },
    onlyIcon: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    // Smaller avatars, e.g. inside selects.
    small: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    lazyload: {
      type: Boolean,
      required: false,
      default: () => true,
    },
  },
  computed: {
    inactive() {
      if (this.sourceProfile !== null) {
        return !!this.sourceProfile.deleted_at;
      }

      return (this.sourceProfiles || []).some(sp => sp.deleted_at);
    },
    isSkeleton() {
      return this.sourceProfile === null && (this.sourceProfiles === null || this.sourceProfiles.length === 0);
    },
    computedSourceProfiles() {
      if (this.sourceProfiles !== null) {
        return [ ...this.sourceProfiles ].reverse();
      }

      if (this.sourceProfile !== null) {
        return [ this.sourceProfile ];
      }

      return [];
    },
    computedName() {
      if (this.computedSourceProfiles.length === 0) {
        return '';
      }

      let primaryProfile = this.computedSourceProfiles.find(sp => sp.platform === 'facebook');
      if (!primaryProfile) {
        primaryProfile = this.computedSourceProfiles[0];
      }

      return primaryProfile ? primaryProfile.name : '';
    },
    dateString() {
      if (this.date === null) {
        return null;
      }

      const dateString = formatToStringYMD(this.date, this.tr) + ' ' + formatTime(this.date);

      if (this.dateTooltipPrefix) {
        return this.dateTooltipPrefix + ' ' + dateString;
      }

      return dateString;
    },
    dateAgoString() {
      if (this.date === null) {
        return null;
      }

      return timeAgo(this.date, this.tr);
    },
  },
};
</script>
